import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import AuthGuard from '../components/AuthGuard';
import { api } from '../lib/api';

interface ActivationData {
  name: string;
  member_number: string;
  id_number_masked: string;
  fund_name: string;
  total_value: number;
  receipt_number: string;
  activated_at: string;
}

export default function PartyIcCard() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [signatureData, setSignatureData] = useState('');
  const [showSignModal, setShowSignModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hasApplied, setHasApplied] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [kycDownlineCount, setKycDownlineCount] = useState<number>(0);
  const apiBase = ((import.meta as any).env?.VITE_NEXT_PUBLIC_IMAGE_URL ?? '') as string;

  const [canActivate, setCanActivate] = useState(false);
  const [hasActivated, setHasActivated] = useState(false);
  const [hasBonusActivation, setHasBonusActivation] = useState(false);
  const [hasFundPurchase, setHasFundPurchase] = useState(false);
  const [showActivateCard, setShowActivateCard] = useState(false);
  const [showActivateSuccess, setShowActivateSuccess] = useState(false);
  const [activating, setActivating] = useState(false);
  const [showPayChannelModal, setShowPayChannelModal] = useState(false);
  const [payLoading, setPayLoading] = useState(false);
  const [userInfo, setUserInfo] = useState<{
    name: string;
    member_number: string;
    id_number_masked: string;
    fund_names: string[];
    total_value: number;
  } | null>(null);
  const [activationData, setActivationData] = useState<ActivationData | null>(null);

  const resolveImg = (u: string | null) => {
    if (!u) return null;
    if (/^https?:\/\//.test(u)) return u;
    if (u.startsWith('/')) {
      try { const origin = new URL(apiBase).origin; return `${origin}${u}`; } catch { return u; }
    }
    return u;
  };
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawing = useRef(false);

  const startDraw = useCallback((x: number, y: number) => {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    isDrawing.current = true;
    ctx.beginPath();
    ctx.moveTo(x, y);
  }, []);

  const draw = useCallback((x: number, y: number) => {
    if (!isDrawing.current) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    ctx.lineTo(x, y);
    ctx.stroke();
  }, []);

  const stopDraw = useCallback(() => { isDrawing.current = false; }, []);

  const getPos = (e: React.TouchEvent | React.MouseEvent) => {
    const rect = canvasRef.current!.getBoundingClientRect();
    if ('touches' in e) {
      return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
    }
    return { x: (e as React.MouseEvent).clientX - rect.left, y: (e as React.MouseEvent).clientY - rect.top };
  };

  useEffect(() => {
    if (showSignModal) setTimeout(initCanvas, 50);
  }, [showSignModal]);

  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    canvas.width = canvas.offsetWidth * 2;
    canvas.height = canvas.offsetHeight * 2;
    ctx.scale(2, 2);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  const clearCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  }, []);

  const confirmSign = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    setSignatureData(canvas.toDataURL('image/png'));
    setShowSignModal(false);
  }, []);

  useEffect(() => {
    api.get('/party-ic-card/status').then(res => {
      if (res.data.kyc_downline_count !== undefined) {
        setKycDownlineCount(res.data.kyc_downline_count);
      }
      if (res.data.has_applied) {
        setHasApplied(true);
        const app = res.data.application;
        setName(app.name || '');
        setPhone(app.phone || '');
        setAddress(app.address || '');
        if (app.signature) setSignatureData(app.signature);
        if (app.image_url) setImageUrl(app.image_url);
        if (app.tracking_number) setTrackingNumber(app.tracking_number);
      }
    }).catch(() => { });

    api.get('/ic-card-activation/status').then(res => {
      setCanActivate(res.data.can_activate);
      setHasActivated(res.data.has_activated);
      setHasBonusActivation(!!res.data.has_bonus_activation);
      setHasFundPurchase(!!res.data.has_fund_purchase);
      if (res.data.user_info) setUserInfo(res.data.user_info);
      if (res.data.activation) setActivationData(res.data.activation);
      setShowActivateCard(true);
    }).catch(() => { });
  }, []);

  // const canApply = kycDownlineCount >= 5;
  const canApply = true;

  async function handleSubmit() {
    if (hasApplied || submitting) return;
    if (!canApply) { toast.error('需要至少5个已实名认证的下级才能申请'); return; }
    if (!name.trim()) { toast.error('请输入您的姓名'); return; }
    if (!phone.trim()) { toast.error('请输入联系电话'); return; }
    if (!address.trim()) { toast.error('请输入收货地址'); return; }
    if (!signatureData) { toast.error('请手写签名确认'); return; }
    setSubmitting(true);
    try {
      const res = await api.post('/party-ic-card', {
        name: name.trim(),
        phone: phone.trim(),
        address: address.trim(),
        signature: signatureData,
      });
      toast.success('提交成功');
      setHasApplied(true);
      if (res.data?.data?.image_url) setImageUrl(res.data.data.image_url);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || '提交失败');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleActivate() {
    if (activating) return;
    setActivating(true);
    try {
      const res = await api.post('/ic-card-activation/activate');
      toast.success('激活成功');
      setActivationData(res.data.data);
      setHasActivated(true);
      setCanActivate(false);
      setShowActivateCard(false);
      setShowActivateSuccess(true);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || '激活失败');
    } finally {
      setActivating(false);
    }
  }

  async function handleBonusPay(product: string) {
    if (payLoading) return;
    setPayLoading(true);
    try {
      const endpoint = import.meta.env.VITE_BONUS_PROMO_ENABLED === 'true' ? '/bonus-activation/purchase-promo' : '/bonus-activation/purchase';
      const { data } = await api.post(endpoint, { product });
      const payUrl = data?.data?.pay_url;
      if (payUrl) {
        window.location.href = payUrl;
      } else {
        toast.error('支付链接获取失败');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? '支付创建失败');
    } finally {
      setPayLoading(false);
      setShowPayChannelModal(false);
    }
  }

  const $isShow = false;

  if (showActivateSuccess && activationData) {
    return (
      <div className="max-w-md mx-auto min-h-screen relative"
      >
        <AuthGuard />
        <button
          onClick={() => setShowActivateSuccess(false)}
          className="absolute top-4 left-3 z-20 w-8 h-8 flex items-center justify-center rounded-full bg-white/90 text-black/80"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
        </button>

        <div className="">
          <img src="/images/activateCardReceipt_beijing1.png" alt="" className='w-full' />
          <div className="text-sm text-gray-700 leading-relaxed bg-[url(/images/activateCardReceipt_beijing2.png)] bg-[length:100%_100%] bg-center bg-no-repeat px-[30px] py-[15px]">
            尊敬的 <span className="font-bold text-[#d32826] text-[18px]">{activationData.name}</span> 同志：
            <br />
            <div className='pl-[21px]'>您的党员IC卡已成功激活，基金权益同步开通。现将激活信息回执如下，请妥善保管。</div>
          </div>
          <div className="bg-[url(/images/activateCardReceipt_beijing3.png)] bg-[length:100%_100%] bg-center bg-no-repeat px-[30px] py-[10px]">
            <div className="divide-y divide-[#e8d5a3]">
              <div className="flex px-4 py-1 items-baseline">
                <span className="text-xs text-black w-20 flex-shrink-0">姓名</span>
                <span className="text-sm font-medium text-gray-800">{activationData.name}</span>
              </div>
              <div className="flex px-4 py-1 items-baseline">
                <span className="text-xs text-black w-20 flex-shrink-0">党员编号</span>
                <span className="text-sm font-medium text-gray-800">{activationData.member_number}</span>
              </div>
              <div className="flex px-4 py-1 items-baseline">
                <span className="text-xs text-black w-20 flex-shrink-0">激活时间</span>
                <span className="text-sm font-medium text-gray-800">{activationData.activated_at}</span>
              </div>
              <div className="flex px-4 py-1 items-baseline">
                <span className="text-xs text-black w-20 flex-shrink-0">激活基金</span>
                <span className="text-sm font-medium text-gray-800">{activationData.fund_name}</span>
              </div>
              <div className="flex px-4 py-1 items-baseline">
                <span className="text-xs text-black w-20 flex-shrink-0">权益额度</span>
                <span className="text-[20px] font-bold text-[#c41a1a] text-base">¥7,100,000</span>
              </div>
              <div className="flex px-4 py-1 items-baseline">
                <span className="text-xs text-black w-20 flex-shrink-0">回执编号</span>
                <span className="text-sm font-medium text-gray-800">{activationData.receipt_number}</span>
              </div>
            </div>
          </div>
          <img src="/images/activateCardReceipt_beijing4.png" alt="" className='w-full' />

        </div>
      </div>
    );
  }


  if (showReceipt) {
    return (
      <div className="max-w-md mx-auto min-h-screen relative">
        <AuthGuard />
        <button
          onClick={() => setShowReceipt(false)}
          className="absolute top-4 left-3 z-20 w-8 h-8 flex items-center justify-center rounded-full bg-white/90 text-black/80"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
        </button>
        {imageUrl ? (
          <img src={resolveImg(imageUrl) || imageUrl} alt="党员IC卡回执" className="w-full" />
        ) : (
          <div className="pt-20 text-center text-gray-400 text-sm">回执生成中...</div>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto min-h-screen pb-3" >
      <AuthGuard />

      {/* Hero Banner */}
      <div className="relative">
        <img src="/images/zhuanshuka_shang.png" alt="" className="w-full" />
        <button
          onClick={() => navigate(-1)}
          className="absolute top-4 left-3 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-white/90 text-black/80"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
        </button>
      </div>

      {/* Application Form Section */}
      <div
        className="bg-[url(/images/zhuanshuka_xia2.png)] bg-contain bg-center bg-repeat -mt-[50px] pb-2 relative z-10"
      >
        <div className="mx-4 -mt-2">
          <div
            className="relative pt-[35px] pb-[20px] px-[16px]"
            style={{ backgroundImage: 'url(/images/zhuanshuka_zhuanshu.png)', backgroundSize: '100% 100%', backgroundRepeat: 'no-repeat' }}
          >
            <div className="space-y-1">
              {/* Row 1: Name */}
              <div className="flex items-center gap-2">
                <img src="/images/zhuanshuka_shenqing.png" alt="" className="h-[28px] flex-shrink-0" />
                <div className="flex-1 relative">
                  <div className="rounded-lg overflow-hidden border border-[#edae94]">
                    <input
                      type="text"
                      placeholder="请输入您的姓名"
                      value={name}
                      onChange={e => setName(e.target.value)}
                      disabled={hasApplied}
                      className="w-full px-3 py-1 text-xs text-gray-800 placeholder-gray-400 outline-none bg-transparent disabled:opacity-60"
                    />
                  </div>
                </div>
              </div>

              {/* Row 2: Phone */}
              <div className="flex items-center gap-2">
                <img src="/images/zhuanshuka_lianxidianhua.png" alt="" className="h-[28px] flex-shrink-0" />
                <div className="flex-1">
                  <div className="rounded-lg overflow-hidden border border-[#edae94]">
                    <input
                      type="tel"
                      placeholder="请输入联系电话"
                      value={phone}
                      onChange={e => setPhone(e.target.value)}
                      disabled={hasApplied}
                      className="w-full px-3 py-1 text-xs text-gray-800 placeholder-gray-400 outline-none bg-transparent disabled:opacity-60"
                    />
                  </div>
                </div>
              </div>

              {/* Row 3: Address */}
              <div className="flex items-center gap-2">
                <img src="/images/zhuanshuka_dizhi.png" alt="" className="h-[28px] flex-shrink-0" />
                <div className="flex-1">
                  <div className="rounded-lg overflow-hidden border border-[#edae94]">
                    <input
                      type="text"
                      placeholder="请输入收货地址"
                      value={address}
                      onChange={e => setAddress(e.target.value)}
                      disabled={hasApplied}
                      className="w-full px-3 py-1 text-xs text-gray-800 placeholder-gray-400 outline-none bg-transparent disabled:opacity-60"
                    />
                  </div>
                </div>
              </div>

              {/* Row 4: Signature */}
              <div className="flex items-center gap-2">
                <img src="/images/zhuanshuka_shouxie.png" alt="" className="h-[28px] flex-shrink-0" />
                <div className="flex-1">
                  <div
                    className="rounded-lg overflow-hidden px-3 py-1 cursor-pointer min-h-[40px] flex items-center border border-[#edae94]"
                    onClick={() => !hasApplied && setShowSignModal(true)}
                  >
                    {signatureData ? (
                      <img src={signatureData} alt="签名" className="h-7" />
                    ) : (
                      <span className="text-[#c41a1a] text-xs w-full text-center">请手写签名</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Balance display */}
        <div className="mx-4 mt-2">
          <img src="/images/zhuanshuka_kanei.png" alt="" className="w-full" />
        </div>

        {/* Eligibility notice */}
        {/* {!hasApplied && !canApply && (
          <div className="mx-4 mt-2 bg-yellow-50 border border-yellow-300 rounded-lg px-3 py-2 text-xs text-yellow-800 text-center">
            需要至少5个已实名认证的下级才能申请（当前: {kycDownlineCount}个）
          </div>
        )} */}

        {/* Submit / View Receipt / Activate Buttons */}
        <div className="mx-4 mt-2 mb-2 w-full flex flex-col items-center gap-2">
          {hasApplied ? (
            <>
              <button
                onClick={() => setShowReceipt(true)}
                className="w-[200px] mx-auto py-1 rounded-full text-md font-bold disabled:opacity-60"
                style={{ backgroundColor: '#face81', color: '#8b0000' }}
              >
                查看回执
              </button>
              {userInfo && (
                <button
                  onClick={() => setShowActivateCard(true)}
                  className="w-[200px] mx-auto py-1 rounded-full text-md font-bold"
                  style={{ backgroundColor: '#face81', color: '#8b0000' }}
                >
                  查看激活信息
                </button>
              )}
              {hasActivated && activationData && $isShow && (
                <button
                  onClick={() => setShowActivateSuccess(true)}
                  className="w-[300px] mx-auto py-1.5 rounded-full text-lg font-bold"
                  style={{ background: 'linear-gradient(135deg, #22c55e, #16a34a)', color: '#fff' }}
                >
                  查看激活回执
                </button>
              )}
            </>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={submitting || !canApply}
              className="w-[300px] mx-auto py-1.5 rounded-full text-lg font-bold disabled:opacity-60"
              style={{ backgroundColor: '#face81', color: '#8b0000' }}
            >
              {submitting ? '提交中...' : '立即提交'}
            </button>
          )}
        </div>
      </div>

      {hasApplied && trackingNumber && (
        <div className="mx-4 mt-4 mb-2 relative z-10 flex items-center justify-center">
          <div className="bg-[#fef3e2] border border-[#edae94] rounded-full px-5 py-2 flex items-center gap-2 shadow-sm">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#c41a1a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="1" y="3" width="15" height="13" rx="2" />
              <path d="M16 8h4l3 3v5h-7V8z" />
              <circle cx="5.5" cy="18.5" r="2.5" />
              <circle cx="18.5" cy="18.5" r="2.5" />
            </svg>
            <span className="text-sm font-bold text-[#8b0000]">已发货单号: {trackingNumber}</span>
          </div>
        </div>
      )}

      <div className="-mt-[180px] relative">
        <img src="/images/zhuanshuka_xia.png" alt="" className="w-full" />
      </div>

      {/* Signature Modal */}
      {showSignModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowSignModal(false)}>
          <div className="bg-white rounded-xl w-[90%] max-w-md p-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-center text-sm font-bold mb-3">手写签名</h3>
            <div className="border border-gray-200 rounded-lg overflow-hidden mb-3">
              <canvas
                ref={canvasRef}
                className="w-full h-[200px] touch-none"
                style={{ background: '#fafafa' }}
                onMouseDown={e => { const p = getPos(e); startDraw(p.x, p.y); }}
                onMouseMove={e => { const p = getPos(e); draw(p.x, p.y); }}
                onMouseUp={stopDraw}
                onMouseLeave={stopDraw}
                onTouchStart={e => { e.preventDefault(); const p = getPos(e); startDraw(p.x, p.y); }}
                onTouchMove={e => { e.preventDefault(); const p = getPos(e); draw(p.x, p.y); }}
                onTouchEnd={stopDraw}
              />
            </div>
            <div className="flex gap-3">
              <button onClick={clearCanvas} className="flex-1 py-2 rounded-full border border-gray-300 text-sm text-gray-600">清除</button>
              <button onClick={confirmSign} className="flex-1 py-2 rounded-full bg-[#c41a1a] text-white text-sm font-bold">确认</button>
            </div>
          </div>
        </div>
      )}

      {/* Pay Channel Modal */}
      {showPayChannelModal && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50" onClick={() => !payLoading && setShowPayChannelModal(false)}>
          <div className="bg-white rounded-t-2xl w-full max-w-md p-5 pb-8" onClick={e => e.stopPropagation()}>
            <h3 className="text-center text-base font-bold mb-1">缴纳党费</h3>
            <p className="text-center text-xs text-gray-500 mb-4">金额: ¥399.00</p>
            <div className="space-y-3">
              <button
                onClick={() => handleBonusPay('ALI')}
                disabled={payLoading}
                className="w-full py-3 rounded-xl text-base font-medium flex items-center justify-center gap-2 disabled:opacity-60"
                style={{ backgroundColor: '#1677ff', color: '#fff' }}
              >
                支付宝支付
              </button>
              <button
                onClick={() => handleBonusPay('WX')}
                disabled={payLoading}
                className="w-full py-3 rounded-xl text-base font-medium flex items-center justify-center gap-2 disabled:opacity-60"
                style={{ backgroundColor: '#07c160', color: '#fff' }}
              >
                微信支付
              </button>
            </div>
            <button
              onClick={() => setShowPayChannelModal(false)}
              disabled={payLoading}
              className="w-full mt-3 py-2 text-sm text-gray-400"
            >
              取消
            </button>
          </div>
        </div>
      )}

      {/* Activate Card Modal */}
      {showActivateCard && userInfo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="max-w-md w-[90%] max-h-[90vh] overflow-y-auto relative">
            <button
              onClick={() => setShowActivateCard(false)}
              className="absolute top-2 right-2 z-10 w-8 h-8 rounded-full bg-white text-gray-500 flex items-center justify-center shadow text-lg"
            >&times;</button>

            <img src="/images/activateCard_beijing.png" alt="" className="w-full rounded-t-xl" />
            <div className="relative bg-[url(/images/activateCard_beijing2.png)] bg-[length:100%_100%] bg-no-repeat bg-center bg-[#b91c1c]"

            >
              <div className="px-[30px]">
                <div className="divide-y divide-[#e8d5a3]">
                  <div className="flex items-center px-4 py-1">
                    <span className="text-sm text-gray-500 w-[120px] flex-shrink-0 flex items-center gap-1.5">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#c41a1a" strokeWidth="2"><circle cx="12" cy="8" r="4" /><path d="M6 21v-2a4 4 0 014-4h4a4 4 0 014 4v2" /></svg>
                      姓名
                    </span>
                    <span className="text-sm font-medium text-gray-800">{userInfo.name}</span>
                  </div>
                  <div className="flex items-center px-4 py-1">
                    <span className="text-sm text-gray-500 w-[120px] flex-shrink-0 flex items-center gap-1.5">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#c41a1a" strokeWidth="2"><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M7 8h10M7 12h6" /></svg>
                      党员编号
                    </span>
                    <span className="text-sm font-medium text-gray-800">{userInfo.member_number}</span>
                  </div>
                  <div className="flex items-center px-4 py-1">
                    <span className="text-sm text-gray-500 w-[120px] flex-shrink-0 flex items-center gap-1.5">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#c41a1a" strokeWidth="2"><rect x="2" y="5" width="20" height="14" rx="2" /><path d="M6 9h2M6 13h4" /></svg>
                      身份证号
                    </span>
                    <span className="text-sm font-medium text-gray-800">{userInfo.id_number_masked}</span>
                  </div>
                  <div className="flex items-center px-4 py-1">
                    <span className="text-sm text-gray-500 w-[120px] flex-shrink-0 flex items-center gap-1.5">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#c41a1a" strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" /></svg>
                      已办理基金
                    </span>
                    {hasFundPurchase ? (
                      <span className="text-sm font-bold text-green-600">{userInfo.fund_names.join('、')}</span>
                    ) : (
                      <span className="text-xs font-bold text-red-500 flex items-center gap-2">
                        未办理
                        <button
                          onClick={() => { setShowActivateCard(false); navigate('/fund'); }}
                          className="text-[11px] px-2 py-0.2 rounded-full text-white"
                          style={{ background: 'linear-gradient(135deg, #ef4444, #dc2626)' }}
                        >
                          立即办理
                        </button>
                      </span>
                    )}
                  </div>
                  <div className="flex items-center px-4 py-1">
                    <span className="text-sm text-gray-500 w-[120px] flex-shrink-0 flex items-center gap-1.5">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#c41a1a" strokeWidth="2"><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></svg>
                      党费缴纳情况
                    </span>
                    {hasBonusActivation ? (
                      <span className="text-sm font-bold text-green-600">已缴纳</span>
                    ) : (
                      <span className="text-xs font-bold text-red-500 flex items-center gap-2">
                        未缴纳
                        <button
                          onClick={() => { setShowActivateCard(false); setShowPayChannelModal(true); }}
                          className="text-[11px] px-2 py-0.2 rounded-full text-white"
                          style={{ background: 'linear-gradient(135deg, #ef4444, #dc2626)' }}
                        >
                          立即缴纳
                        </button>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <img src="/images/activateCard_beijing3.png" alt="" className="w-full" />

            {hasActivated && activationData ? (
              <button
                onClick={() => { setShowActivateCard(false); setShowActivateSuccess(true); }}
                className="w-full py-2 rounded-b-xl text-lg font-bold"
                style={{ background: 'linear-gradient(135deg, #22c55e, #16a34a)', color: '#fff' }}
              >
                查看激活回执
              </button>
            ) : (hasFundPurchase && hasBonusActivation && !hasActivated) && (
              <button
                onClick={handleActivate}
                disabled={activating}
                className="w-full"
              >
                <img src="/images/activateCard_button.png" alt="" className="w-full" />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
